import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Home,
  Users,
  Building2,
  Package,
  ShoppingCart,
  Truck,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronDown,
  BookOpen,
  BarChart3,
  Receipt,
  DollarSign,
  TrendingUp,
} from 'lucide-react';

// ── Navigation definitions per role ──────────────────────────────

const getNavItems = (user) => {
  const permisos = user?.permisos;
  const username = user?.username;

  // Admin / SuperAdmin
  if (permisos === 1 || permisos === 6) {
    const items = [
      { label: 'Inicio', path: '/gestion', icon: Home },
      { label: 'Usuarios', path: '/usuarios', icon: Users },
      { label: 'Clientes', path: '/clientes', icon: Building2 },
      { label: 'Productos', path: '/productos', icon: Package },
      { label: 'Gestión Pedidos', path: '/pedidos', icon: ShoppingCart },
      { label: 'Precios', path: '/precios', icon: DollarSign },
      { label: 'Transportes', path: '/transportes/agencias', icon: Truck },
      { label: 'Catálogo', path: '/catalogo', icon: BookOpen },
    ];

    // Gestion Comercial: Admin y SuperAdmin
    const comercialChildren = [
      { label: 'Carga de Albaranes', path: '/comercial/albaranes' },
    ];
    comercialChildren.push({ label: 'Comisiones', path: '/comercial/comisiones' });
    comercialChildren.push({ label: 'Ofertas', path: '/ofertas' });
    items.push({
      label: 'Gestion Comercial',
      icon: TrendingUp,
      children: comercialChildren,
    });

    // Contabilidad: only for admin username or permisos 6
    if (username === 'admin' || permisos === 6) {
      const contabilidadChildren = [
        { label: 'Generar Remesa', path: '/contabilidad/remesas' },
        { label: 'Vencimientos por Recibo Bancario', path: '/contabilidad/remesas-vencimientos' },
        { label: 'Vencimiento por transferencia y otros', path: '/contabilidad/vencimientos' },
        { label: 'Estado Remesas', path: '/contabilidad/estado-remesas' },
        { label: 'Carga de Facturas', path: '/comercial/facturas' },
        { label: 'Facturas', path: '/contabilidad/facturas' },
      ];
      if (permisos === 6 || username === 'admin') {
        contabilidadChildren.push({ label: 'Estadisticas', path: '/comercial/estadisticas' });
      }
      items.push({
        label: 'Contabilidad',
        icon: FileText,
        children: contabilidadChildren,
      });
    }

    return items;
  }

  // Operador
  if (permisos === 2) {
    return [
      { label: 'Inicio', path: '/gestion', icon: Home },
      { label: 'Usuarios', path: '/usuarios', icon: Users },
      { label: 'Clientes', path: '/clientes', icon: Building2 },
      { label: 'Productos', path: '/productos', icon: Package },
      { label: 'Gestión Pedidos', path: '/pedidos', icon: ShoppingCart },
      { label: 'Precios', path: '/precios', icon: DollarSign },
    ];
  }

  // Comercial
  if (permisos === 3) {
    return [
      { label: 'Inicio', path: '/comercial', icon: Home },
      { label: 'Mis Clientes', path: '/comercial/clientes', icon: Building2 },
      { label: 'Ofertas', path: '/comercial/ofertas', icon: TrendingUp },
      { label: 'Catálogo', path: '/catalogo', icon: BookOpen },
    ];
  }

  // Cliente
  if (permisos === 5) {
    // Para clientes: "Inicio" (donde están las pestañas Mis Pedidos, Nuevo
    // Pedido, Facturación, Catálogo) y "Análisis" como página independiente
    // con gráficos y métricas comparativas.
    return [
      { label: 'Inicio', path: '/cliente', icon: Home },
      { label: 'Análisis', path: '/cliente/analisis', icon: BarChart3 },
    ];
  }

  return [];
};

// ── Dropdown component ───────────────────────────────────────────

const NavDropdown = ({ item, isActive }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const Icon = item.icon;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? 'text-white bg-white/20'
            : 'text-white hover:text-gray-200 hover:bg-white/10'
        }`}
      >
        <Icon size={18} />
        <span>{item.label}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          {/* Invisible overlay to close on click outside */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1">
              {item.children.map((child) => (
                <Link
                  key={child.path}
                  to={child.path}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {child.label}
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── Order badges (puntos rojo/verde de pedidos, como el PHP) ─────────
// Rojo  = pedidos nuevos/abiertos (estado 1, 7 dias, sin centralsalud)
// Verde = pedidos confirmados sin albaran (estado 2)
const OrderBadges = ({ stats }) => {
  if (!stats) return null;
  return (
    <>
      {stats.abiertos > 0 && (
        <span
          title="Pedidos nuevos (abiertos)"
          className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold text-white bg-red-600"
        >
          {stats.abiertos}
        </span>
      )}
      {stats.confirmados > 0 && (
        <span
          title="Pedidos confirmados (pendientes de albarán)"
          className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold text-white bg-green-600"
        >
          {stats.confirmados}
        </span>
      )}
    </>
  );
};

// ── Header component ─────────────────────────────────────────────

const Header = () => {
  const { user, logout, isAuthenticated, token, API_URL } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navItems = getNavItems(user);

  // Badges de pedidos: solo para staff (admin/operador/superadmin), que son
  // los roles que pueden consultar /orders/stats. Refresca cada 60s para ver
  // los pedidos que van entrando.
  const permisos = user?.permisos;
  const isStaff = permisos === 1 || permisos === 2 || permisos === 6;
  const [orderStats, setOrderStats] = useState({ abiertos: 0, confirmados: 0 });

  useEffect(() => {
    if (!isAuthenticated || !isStaff) return;
    let active = true;
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/orders/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          if (active) {
            setOrderStats({
              abiertos: d.pedidos_abiertos || 0,
              confirmados: d.pedidos_confirmados || 0
            });
          }
        }
      } catch { /* silencioso */ }
    };
    fetchStats();
    const id = setInterval(fetchStats, 60000);
    return () => { active = false; clearInterval(id); };
  }, [isAuthenticated, isStaff, API_URL, token]);

  const userInitial = user?.nombrec
    ? user.nombrec.charAt(0).toUpperCase()
    : user?.username
      ? user.username.charAt(0).toUpperCase()
      : 'U';

  // Check if a nav item (or any of its children) matches the current path
  const isItemActive = (item) => {
    if (item.path) return location.pathname === item.path;
    if (item.children) return item.children.some((c) => location.pathname === c.path);
    return false;
  };

  return (
    <header className="shadow-md sticky top-0 z-50">
      <div style={{ background: 'linear-gradient(to right, #ffffff 0%, #ffffff 100px, #0099FF 300px)' }}>
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* LEFT: Brand + Navigation */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center mr-10">
                <img src="/logo.png" alt="ComprasSanitarias" className="h-12 w-auto" />
              </Link>

            {isAuthenticated && (
              <nav className="hidden md:flex items-center space-x-1 pl-28">
                {navItems.map((item) => {
                  const active = isItemActive(item);

                  // Dropdown items
                  if (item.children) {
                    return (
                      <NavDropdown key={item.label} item={item} isActive={active} />
                    );
                  }

                  // Simple link items
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        active
                          ? 'text-white bg-white/20'
                          : 'text-white hover:text-gray-200 hover:bg-white/10'
                      }`}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                      {item.path === '/pedidos' && <OrderBadges stats={orderStats} />}
                    </Link>
                  );
                })}
              </nav>
            )}
          </div>

          {/* RIGHT: User info + Logout */}
          {isAuthenticated && (
            <div className="flex items-center space-x-4">
              <span className="text-white text-sm hidden sm:inline">
                {user?.nombrec || user?.username}
              </span>
              <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center">
                <span className="text-sm font-bold" style={{ color: '#0099FF' }}>
                  {userInitial}
                </span>
              </div>
              <button
                onClick={logout}
                className="text-white hover:text-gray-200 transition-colors"
                title="Cerrar sesión"
              >
                <LogOut size={18} />
              </button>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-white hover:text-gray-200 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Mobile Nav Dropdown */}
      {mobileMenuOpen && isAuthenticated && (
        <div className="md:hidden border-t border-white/20 px-4 py-3 space-y-1" style={{ backgroundColor: '#0099FF' }}>
          {navItems.map((item) => {
            // Dropdown children rendered flat on mobile
            if (item.children) {
              return (
                <MobileDropdown
                  key={item.label}
                  item={item}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              );
            }

            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-white hover:bg-white/10 transition-colors"
              >
                <Icon size={18} />
                <span>{item.label}</span>
                {item.path === '/pedidos' && <OrderBadges stats={orderStats} />}
              </Link>
            );
          })}
        </div>
      )}
    </header>
  );
};

// ── Mobile dropdown (expand/collapse) ────────────────────────────

const MobileDropdown = ({ item, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const Icon = item.icon;

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium text-white hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon size={18} />
          <span>{item.label}</span>
        </div>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="ml-9 space-y-1">
          {item.children.map((child) => (
            <Link
              key={child.path}
              to={child.path}
              onClick={onNavigate}
              className="block px-3 py-2 rounded-md text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Header;
